<template>
  <div class="dynamic-form-page">
    <div fluid class="pa-0">
      <!-- Loading State -->
      <v-overlay :value="isLoading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <!-- Banner with Overlay Logo and Header Section -->
      <div
        class="form-media-section"
        v-if="formBanner || formLogo || formHeader || formDescription"
      >
        <div class="banner-container" v-if="formBanner">
          <!-- Banner -->
          <div class="form-banner">
            <img :src="formBanner" alt="Form Banner" class="banner-image" />
          </div>

          <!-- Overlay Logo and Header on top of banner (transparent) -->
          <div
            class="banner-overlay transparent-overlay"
            v-if="formLogo || formHeader || formDescription"
          >
            <div class="overlay-content" :class="{ 'with-logo': formLogo }">
              <div class="d-flex align-center" v-if="formLogo">
                <img :src="formLogo" alt="Form Logo" class="logo-image mr-4 rounded-full" />
                <div>
                  <h2 class="text-h5 font-weight-bold text-white">{{ formHeader }}</h2>
                  <!-- Form Description on banner -->
                  <p class="text-subtitle-1 mb-0 text-white" v-if="formDescription">
                    {{ formDescription }}
                  </p>
                </div>
              </div>
              <div v-else>
                <h2 class="text-h6 font-weight-bold mb-2 text-white">{{ formHeader }}</h2>
                <!-- Form Description on banner -->
                <p class="text-subtitle-2 mb-0 text-white" v-if="formDescription">
                  {{ formDescription }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Fallback if no banner but has header -->
        <div v-else-if="formHeader || formDescription">
          <v-card class="elevation-1 mb-6 rounded-lg pa-4">
            <h2 class="text-h6 font-weight-bold mb-0">{{ formHeader }}</h2>
            <p class="text-subtitle-2 text--secondary mb-0" v-if="formDescription">
              {{ formDescription }}
            </p>
          </v-card>
        </div>
      </div>

      <!-- Link Expired State -->
      <v-container v-if="!isLoading && isExpired" class="py-12">
        <v-row justify="center">
          <v-col cols="12" md="6">
            <v-card class="text-center pa-8 rounded-lg">
              <v-icon size="64" color="red lighten-1" class="mb-4">mdi-clock-alert-outline</v-icon>
              <h3 class="text-h6 mb-2">Link Expired</h3>
              <p class="text--secondary mb-4">
                This form link has expired and is no longer accessible.
              </p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Form Submitted State -->
      <v-container v-if="!isLoading && isSubmitted" class="py-12">
        <v-row justify="center">
          <v-col cols="12" md="6">
            <v-card class="text-center pa-8 rounded-lg">
              <v-icon size="64" color="green lighten-1" class="mb-4"
                >mdi-check-circle-outline</v-icon
              >
              <h3 class="text-h6 mb-2">Form Submitted</h3>
              <p class="text--secondary mb-4">You have already submitted this form.</p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Form Content -->
      <div v-if="!isLoading && formData && !isExpired && !isSubmitted">
        <div class="p-8 form-content-wrapper">
          <v-row>
            <v-col cols="12" md="8" lg="9">
              <!-- Timer Alert -->
              <v-alert
                v-if="timeRemaining"
                dense
                type="info"
                text
                class="mb-6"
                icon="mdi-clock-outline"
              >
                Time Remaining: <strong>{{ timeRemaining }}</strong>
              </v-alert>

              <!-- Progress Indicator -->
              <!-- <v-card class="elevation-1 mb-6 rounded-lg">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="progressPercentage"
                      height="8"
                      rounded
                      color="primary"
                    ></v-progress-linear>
                    <span class="text-caption ml-3">{{ Math.round(progressPercentage) }}%</span>
                  </div>
                </v-card-text>
              </v-card> -->

              <!-- Form Sections -->
              <div v-for="(item, index) in formData" :key="getItemKey(item, index)">
                <NestedQuestionRenderer
                  :item="item"
                  :responses="responses"
                  :disabled="item.disabled"
                  :depth="0"
                  @update-response="updateResponse"
                />
              </div>
            </v-col>

            <!-- Sidebar for larger screens -->
            <v-col cols="12" md="4" lg="3" class="d-none d-md-block form-summary-col">
              <v-card class="elevation-1 rounded-lg form-summary-sticky">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Form Summary
                </v-card-title>
                <v-card-text>
                  <div class="summary-item mb-3">
                    <div class="d-flex justify-space-between">
                      <span>Questions</span>
                      <span>{{ totalQuestions }}</span>
                    </div>
                  </div>
                  <div class="summary-item mb-3">
                    <div class="d-flex justify-space-between">
                      <span>Answered</span>
                      <span>{{ answeredQuestions }}</span>
                    </div>
                  </div>
                  <div class="summary-item mb-4">
                    <div class="d-flex justify-space-between">
                      <span>Completion</span>
                      <span>{{ Math.round(progressPercentage) }}%</span>
                    </div>
                  </div>
                  <!-- Submit Button moved to Form Summary -->
                  <v-btn
                    color="primary"
                    @click="submitForm"
                    :loading="isSubmitting"
                    :disabled="!isFormValid || isLoading"
                    block
                    large
                  >
                    Submit Form
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Empty State -->
      <v-container v-else-if="!isLoading && !formData && !isExpired && !isSubmitted" class="py-12">
        <v-row justify="center">
          <v-col cols="12" md="6">
            <v-card class="text-center pa-8 rounded-lg">
              <v-icon size="64" color="grey lighten-1" class="mb-4"
                >mdi-file-document-outline</v-icon
              >
              <h3 class="text-h6 mb-2">Form Not Found</h3>
              <p class="text--secondary mb-4">
                The form you're looking for doesn't exist or has been removed.
              </p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Mobile Summary (Fixed at bottom for mobile) -->
      <div
        class="mobile-summary d-md-none"
        v-if="!isLoading && formData && !isExpired && !isSubmitted"
      >
        <v-container>
          <div class="d-flex align-center justify-space-between py-3">
            <div>
              <div class="text-caption">{{ answeredQuestions }}/{{ totalQuestions }} answered</div>
              <div class="text-caption">{{ Math.round(progressPercentage) }}% complete</div>
            </div>
            <!-- Submit Button moved to Mobile Summary -->
            <v-btn
              color="primary"
              small
              @click="submitForm"
              :loading="isSubmitting"
              :disabled="!isFormValid"
            >
              Submit
            </v-btn>
          </div>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script>
import QuestionField from '@/components/Workforce/Settings/OrgConfigComponents/QuestionField.vue'
import NestedQuestionRenderer from '@/components/Workforce/Settings/OrgConfigComponents/NestedQuestionRenderer.vue'
import { calculateRemainingTime } from '@/utils/common'

export default {
  name: 'DynamicFormPage',
  components: {
    // QuestionField,
    NestedQuestionRenderer
  },
  props: {
    questionResponses: {
      type: Array,
      default: () => []
    },
    responseId: {
      type: String,
      default: null
    },
    formImages: {
      type: Array,
      default: () => []
    },
    responseHeader: {
      type: String,
      default: ''
    },
    initialActivities: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formId: null,
      formData: null,
      responses: {},
      isSubmitting: false,
      isLoading: false,
      formTitle: 'Dynamic Form',
      formDescription: '',
      formHeader: '',
      formBanner: null,
      formLogo: null,
      isFormBuilder: false,
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL || '',
      // allQuestions: null,
      allQuestions: null,
      activities: [],
      isExpired: false,
      isSubmitted: false,
      timeRemaining: '',
      timer: null
    }
  },
  computed: {
    totalQuestions() {
      if (!this.formData) return 0
      let count = 0
      this.formData.forEach((item) => {
        if (this.isSection(item)) {
          const validSubQuestions = item.subQuestions.filter(
            (subItem) => typeof subItem.questionType !== 'object'
          )
          count += validSubQuestions.length
        } else {
          count += 1
        }
      })
      console.log(this.formImages, 'aaaa')
      return count
    },
    answeredQuestions() {
      return Object.values(this.responses).filter((response) => {
        if (response === null || response === undefined) return false
        if (Array.isArray(response)) return response.length > 0
        if (typeof response === 'string') return response.trim() !== ''
        return true
      }).length
    },
    progressPercentage() {
      if (this.totalQuestions === 0) return 0
      return (this.answeredQuestions / this.totalQuestions) * 100
    },
    isFormValid() {
      if (!this.formData) return false

      for (const item of this.formData) {
        if (this.isSection(item)) {
          const validSubQuestions = item.subQuestions.filter(
            (subItem) => typeof subItem.questionType !== 'object'
          )
          for (const subItem of validSubQuestions) {
            if (subItem.isRequired && !this.isQuestionAnswered(subItem._id)) {
              return false
            }
          }
        } else {
          if (item.isRequired && !this.isQuestionAnswered(item._id)) {
            return false
          }
        }
      }
      return true
    }
  },
  watch: {
    questionResponses: {
      handler(val) {
        if (val && val.length > 0) {
          this.setupViewMode()
        }
      },
      deep: true
    },
    responses: {
      handler() {
        this.handleDependencyChange()
      },
      deep: true
    }
  },
  methods: {
    setupViewMode() {
      if (this.questionResponses && this.questionResponses.length > 0) {
        this.formData = JSON.parse(JSON.stringify(this.questionResponses))

        // Populate activities from prop
        if (this.initialActivities && this.initialActivities.length > 0) {
          this.activities = JSON.parse(JSON.stringify(this.initialActivities))
        }

        // Handle form header and images for view mode
        if (this.responseHeader) {
          this.formHeader = this.responseHeader
        }

        if (this.formImages && Array.isArray(this.formImages)) {
          const bannerImage = this.formImages.find((img) => img.type === 'banner')
          const logoImage = this.formImages.find((img) => img.type === 'logo')

          if (bannerImage) {
            this.formBanner = this.processImageUrl(bannerImage.path)
          } else {
            this.formBanner = null
          }

          if (logoImage) {
            this.formLogo = this.processImageUrl(logoImage.path)
          } else {
            this.formLogo = null
          }
        }

        // Fetch dynamic options even when viewing existing responses
        if (localStorage.getItem('isAuthenticatedUser') === 'true') {
          this.fetchDynamicOptions()
        }

        this.$nextTick(() => {
          this.initializeResponsesFromData(this.formData)
          this.handleDependencyChange()
        })
      }
    },
    initializeResponsesFromData(formData) {
      if (!formData || !Array.isArray(formData)) return

      const processItem = (item) => {
        if (item.userResponse === undefined) return

        const questionId = item._id
        const userResponse = item.userResponse

        if (item.questionType === 'CHECKBOX' && Array.isArray(userResponse)) {
          // Handle checkbox responses
          const checkboxResponse = {}
          if (item.options && Array.isArray(item.options)) {
            item.options.forEach((option) => {
              checkboxResponse[option] = userResponse.some((resp) => {
                if (typeof resp === 'string') {
                  return resp === option
                } else if (typeof resp === 'object' && resp !== null) {
                  try {
                    return JSON.stringify(resp) === option || resp.toString() === option
                  } catch (e) {
                    return false
                  }
                }
                return false
              })
            })
          }
          this.$set(this.checkboxResponses, questionId, checkboxResponse)
          this.$set(this.responses, questionId, userResponse)
        } else if (Array.isArray(userResponse) && userResponse.length > 0) {
          // For other array responses, use the first element
          this.$set(this.responses, questionId, userResponse[0])
        } else if (userResponse !== null && userResponse !== undefined) {
          this.$set(this.responses, questionId, userResponse)
        }
      }

      formData.forEach((item) => {
        if (this.isSection(item)) {
          // Handle section items
          if (item.subQuestions && Array.isArray(item.subQuestions)) {
            item.subQuestions.forEach(processItem)
          }
        } else {
          // Handle individual questions
          processItem(item)
        }
      })
    },
    isSection(item) {
      return item.sectionName && Array.isArray(item.subQuestions)
    },
    getItemKey(item, index) {
      return item._id || item.sectionName || `item-${index}`
    },
    isQuestionAnswered(questionId) {
      const response = this.responses[questionId]
      if (response === null || response === undefined) return false
      if (Array.isArray(response)) return response.length > 0
      if (typeof response === 'string') return response.trim() !== ''
      return true
    },
    updateResponse(questionId, value) {
      this.$set(this.responses, questionId, value)
    },
    goBack() {
      this.$router.go(-1)
    },
    createNestedResponseStructure() {
      const sourceData = this.allQuestions || this.formData
      const questionsAnswered = JSON.parse(JSON.stringify(sourceData))

      const sanitizeItem = (item) => {
        // Ensure options are strings if they exist
        if (item.options && Array.isArray(item.options)) {
          item.options = item.options.map((opt) => {
            if (typeof opt === 'object' && opt !== null) {
              return JSON.stringify(opt) // Stringify object options to satisfy [String] schema
            }
            return String(opt)
          })
        }

        // Handle userResponse formatting
        if (this.responses[item._id] !== undefined) {
          item.userResponse = this.formatResponse(this.responses[item._id], item.questionType)
        } else {
          item.userResponse = []
        }
      }

      questionsAnswered.forEach((item) => {
        if (this.isSection(item)) {
          // if (!item.questionType) item.questionType = 'SECTION'
          // if (!item.question) item.question = item.sectionName

          if (item.subQuestions && Array.isArray(item.subQuestions)) {
            item.subQuestions.forEach((subItem) => {
              sanitizeItem(subItem)
            })
          }
        } else {
          sanitizeItem(item)
        }
      })

      return questionsAnswered
    },

    formatResponse(response) {
      if (response === null || response === undefined) {
        return []
      }

      if (Array.isArray(response)) {
        // For checkbox responses or file uploads
        return response.map((item) => {
          if (typeof item === 'object') {
            return JSON.stringify(item)
          }
          return String(item)
        })
      }

      // For text, number, select, radio responses
      if (typeof response === 'object') {
        return [JSON.stringify(response)]
      }

      return [String(response)]
    },

    prepareSubmissionPayload(activityName) {
      const userData = this.$storage.getUniversal('user')
      const newActivity = {
        activityName: activityName,
        updatedBy: userData?._id
      }

      const currentActivities = this.activities ? [...this.activities] : []
      currentActivities.push(newActivity)

      return {
        questionsAnswered: this.createNestedResponseStructure(),
        activities: currentActivities
      }
    },

    filterQuestions(questions) {
      const isAuthenticated = localStorage.getItem('isAuthenticatedUser') === 'true'

      return questions.reduce((acc, item) => {
        if (typeof item.questionType === 'object') return acc

        if (isAuthenticated) {
          acc.push(item)
        } else {
          if (this.isSection(item)) {
            const filteredSubQuestions = item.subQuestions.filter(
              (q) => q.audience !== 'OFFICE' && typeof q.questionType !== 'object'
            )

            if (filteredSubQuestions.length > 0) {
              acc.push({
                ...item,
                subQuestions: filteredSubQuestions
              })
            }
          } else {
            if (item.audience !== 'OFFICE') {
              acc.push(item)
            }
          }
        }
        return acc
      }, [])
    },

    processFormImages(images) {
      if (images && Array.isArray(images)) {
        const bannerImage = images.find((img) => img.type === 'banner')
        const logoImage = images.find((img) => img.type === 'logo')

        if (bannerImage) {
          this.formBanner = this.processImageUrl(bannerImage.path)
        } else {
          this.formBanner = null
        }

        if (logoImage) {
          this.formLogo = this.processImageUrl(logoImage.path)
        } else {
          this.formLogo = null
        }
      }
    },

    async handleFormLoad(form, questionsKey) {
      if (!form) return

      this.formDescription = form.linkedFormDetails?.header || form.header || ''
      this.formHeader = form.linkedFormDetails?.categoryName || form.categoryName || ''
      this.activities = form.activities || []

      const questions = form[questionsKey]
      if (questions && Array.isArray(questions)) {
        this.allQuestions = JSON.parse(JSON.stringify(questions))
        this.formData = this.filterQuestions(questions)
      }

      if (localStorage.getItem('isAuthenticatedUser') === 'true') {
        await this.fetchDynamicOptions()
      }

      this.$nextTick(() => {
        this.initializeResponsesFromData(this.formData)
        this.handleDependencyChange()
      })

      this.processFormImages(form.linkedFormDetails?.images)
      this.checkAccessStatus(form)
    },

    async submitForm() {
      this.isSubmitting = true
      try {
        const orgId = this.$route.params?.orgId
        const resId = this.$route.query.responseId

        if (!orgId && this.$props.responseId) {
          await this.updateFormResponse()
          return
        }

        const submissionData = this.prepareSubmissionPayload('USER_FORM_SUBMITTED')

        let submitUrl = ``
        if (orgId) {
          submitUrl = `/orgSetup/open/form-responses/${orgId}/${resId}`
        }
        await this.$axios.put(submitUrl, submissionData)

        this.$toast.success('Form submitted successfully!')
        this.$router.push('/workforce/form-success')
      } catch (error) {
        console.error('Error submitting form:', error)
        this.$toast.error('Failed to submit form. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },
    async updateFormResponse() {
      try {
        const responseId = this.$props.responseId
        const updateData = this.prepareSubmissionPayload('FORM_RESPONSE_UPDATED')
        let updateUrl = `/orgSetup/form-response/${responseId}`
        await this.$axios.put(updateUrl, updateData)
        this.$toast.success('Form response updated successfully!')
        this.$emit('response-updated')
      } catch (error) {
        console.log(error)
      }
    },
    async loadResponseData() {
      this.isLoading = true
      try {
        const formId = this.$route.query.id
        const responseId = this.$route.query.responseId
        const orgId = this.$route.params?.orgId

        let apiUrl = `` // Default URL
        if (orgId && responseId) {
          apiUrl = `/orgSetup/open/form-responses/${orgId}?responseId=${responseId}`
        }
        if (orgId && formId) {
          apiUrl = `/orgSetup/form/${orgId}/${formId}`
        }

        const response = await this.$axios.get(apiUrl)
        const form = response.data?.responses[0]

        await this.handleFormLoad(form, 'questionsAnswered')
      } catch (error) {
        console.error('Error loading form data:', error)
      } finally {
        this.isLoading = false
      }
    },

    async loadFormData() {
      this.isLoading = true
      try {
        const formId = this.$route.query.id
        const orgId = this.$route.params?.orgId

        let apiUrl = `` // Default URL
        if (orgId && formId) {
          apiUrl = `/orgSetup/form/${orgId}/${formId}`
        }

        const response = await this.$axios.get(apiUrl)
        const form = response.data?.data

        await this.handleFormLoad(form, 'questions')
      } catch (error) {
        console.error('Error loading form data:', error)
      } finally {
        this.isLoading = false
      }
    },

    // Helper method to process image URLs with s3BaseUrl
    processImageUrl(url) {
      if (!url) return null

      if (url.startsWith('http')) {
        return url
      }

      if (this.s3BaseUrl && !url.startsWith('/')) {
        return this.s3BaseUrl + url
      }

      return url
    },
    async fetchDynamicOptions() {
      if (!this.formData) return

      const fetchPromises = []

      const processQuestion = (question) => {
        const url = question.fetchUrl

        if (url) {
          fetchPromises.push(
            this.$axios
              .get(url)
              .then((response) => {
                let rawOptions = []
                const data = response.data

                if (Array.isArray(data.data)) {
                  rawOptions = data.data
                } else if (Array.isArray(data)) {
                  rawOptions = data
                } else if (typeof data === 'object' && data !== null) {
                  const possibleArray = Object.values(data).find((val) => Array.isArray(val))
                  if (possibleArray) {
                    rawOptions = possibleArray
                  }
                }

                if (Array.isArray(rawOptions)) {
                  this.$set(question, 'rawOptions', rawOptions)
                  const mappedOptions = this.mapOptions(rawOptions, question)
                  this.$set(question, 'options', mappedOptions)
                }
              })
              .catch((err) => {
                console.error(`Failed to fetch options for question ${question._id}:`, err)
              })
          )
        }
      }

      this.formData.forEach((item) => {
        if (this.isSection(item)) {
          item.subQuestions.forEach(processQuestion)
        } else {
          processQuestion(item)
        }
      })

      await Promise.all(fetchPromises)
      this.handleDependencyChange()
    },

    mapOptions(rawOptions, question) {
      const labelKey = question.labelKey || 'name'
      const valueKey = question.valueKey || '_id'

      return rawOptions.map((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          return {
            text: opt[labelKey] || opt.name || opt.title || opt,
            value: opt[valueKey] || opt._id || opt.id || opt,
            original: opt
          }
        }
        return opt
      })
    },

    findQuestionByMappedKey(key) {
      if (!this.formData) return null
      for (const item of this.formData) {
        if (this.isSection(item)) {
          const found = item.subQuestions.find((q) => q.mappedKeyField === key)
          if (found) return found
        } else {
          if (item.mappedKeyField === key) return item
        }
      }
      return null
    },

    updateQuestionOptions(question) {
      // const optionTypes = ['SELECT_OPTIONS', 'RADIO', 'CHECKBOX', 'DATA_CATEGORY']
      // if (!optionTypes.includes(question.questionType)) return

      if (!question.ref) {
        this.$set(question, 'disabled', false)
        return
      }

      const refQuestion = this.findQuestionByMappedKey(question.ref)
      if (!refQuestion) {
        this.$set(question, 'disabled', false)
        return
      }

      const refValue = this.responses[refQuestion._id]

      if (!refValue) {
        this.$set(question, 'disabled', true)
        // Clear value if referenced question is cleared
        if (this.responses[question._id]) {
          this.$set(this.responses, question._id, null)
        }
        return
      }

      if (question.rawOptions) {
        const filteredRaw = question.rawOptions.filter((opt) => {
          const refKey = question.ref
          const refProp = opt[refKey]

          if (!refProp) return false

          if (typeof refProp === 'object' && refProp !== null) {
            return refProp._id === refValue || refProp.id === refValue
          }
          return refProp === refValue
        })

        const mappedOptions = this.mapOptions(filteredRaw, question)
        this.$set(question, 'options', mappedOptions)

        // Check if current response is valid
        const currentResponse = this.responses[question._id]
        if (currentResponse) {
          const isValid = mappedOptions.some((opt) => opt.value === currentResponse)
          if (!isValid) {
            this.$set(this.responses, question._id, null)
          }
        }
      }

      this.$set(question, 'disabled', false)
    },

    handleDependencyChange() {
      if (!this.formData) return
      this.formData.forEach((item) => {
        if (this.isSection(item)) {
          item.subQuestions.forEach((subItem) => this.updateQuestionOptions(subItem))
        } else {
          this.updateQuestionOptions(item)
        }
      })
    },

    checkAccessStatus(form) {
      const isAuthenticated = localStorage.getItem('isAuthenticatedUser') === 'true'
      if (isAuthenticated) return

      // Check if form is already submitted
      if (form.activities?.some((a) => a.activityName === 'USER_FORM_SUBMITTED')) {
        this.isSubmitted = true
        return
      }

      const expiryDays = form.metaData?.expiry

      if (expiryDays && form?.createdAt) {
        const expiryTime = new Date(form.createdAt).getTime() + expiryDays * 24 * 60 * 60 * 1000

        const updateTimer = () => {
          const result = calculateRemainingTime(expiryTime)

          if (result.isExpired) {
            this.isExpired = true
            this.timeRemaining = ''
            if (this.timer) clearInterval(this.timer)
          } else {
            this.isExpired = false
            this.timeRemaining = result.timeString
          }
        }

        updateTimer()
        this.timer = setInterval(updateTimer, 1000)
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  async mounted() {
    this.formId = this.$route.query?.id
    if (this.formId) {
      await this.loadFormData()
    }
    if (this.$props.questionResponses && this.$props.questionResponses.length > 0) {
      this.setupViewMode()
      return
    }
    await this.loadResponseData()
  }
}
</script>

<style scoped>
.dynamic-form-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.form-header {
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid #e0e0e0;
}

.form-content-wrapper {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-bottom: 80px;
}

.banner-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.banner-container {
  position: relative;
  width: 100%;
}

.form-banner {
  width: 100%;
}

.banner-overlay {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 10;
}

.transparent-overlay {
  background: transparent !important;
  box-shadow: none !important;
}

.overlay-content {
  max-width: 500px;
  padding: 16px;
  border-radius: 4px;
}

.overlay-content.with-logo {
  max-width: 600px;
}

.text-white {
  color: white !important;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.logo-image {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.form-description {
  margin-top: 8px;
  font-size: 0.9rem;
}

.section-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px !important;
}

.section-content {
  padding: 16px;
}

.question-wrapper {
  padding: 16px 0;
  border-bottom: 1px solid #eee;
}

.question-wrapper:last-child {
  border-bottom: none;
}

.mobile-summary {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 1px solid #e0e0e0;
  z-index: 100;
}

.summary-item {
  padding: 8px 0;
}

.summary-item:not(:last-child) {
  border-bottom: 1px solid #eee;
}

/* Sticky Form Summary */
.form-summary-col {
  position: relative;
}

.form-summary-sticky {
  position: sticky;
  top: 20px;
  z-index: 10;
}

/* Card hover effects similar to QuestionPreview */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

/* Section container styling */
.section-card {
  transition: all 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .section-header {
    padding: 12px 16px !important;
  }

  .section-content {
    padding: 12px;
  }

  .question-wrapper {
    padding: 12px 0;
  }

  .form-content-wrapper {
    max-height: calc(100vh - 200px);
  }
}

@media (max-width: 600px) {
  .form-header .text-h5 {
    font-size: 1.25rem !important;
  }

  .banner-image {
    height: 150px;
  }

  .logo-image {
    width: 50px;
    height: 50px;
  }

  .banner-overlay {
    left: 8px;
    right: 8px;
    top: 8px;
  }

  .overlay-content {
    max-width: none;
    padding: 12px;
  }

  .overlay-content.with-logo {
    max-width: none;
  }
}
</style>
